import { MERGE_FREEZE_REASON_MAX_LENGTH } from '@dev-console/deployments';
import type { MergeFreezeResponse } from '@dev-console/types';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

import { setMergeFreeze } from '@/config/api-helpers';

interface MergeFreezeDialogProps {
  enabled: boolean;
  onClose: () => void;
  onSuccess: (result: MergeFreezeResponse) => void;
  onError: (err: unknown) => void;
}

export function MergeFreezeDialog({ enabled, onClose, onSuccess, onError }: MergeFreezeDialogProps) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<MergeFreezeResponse | null>(null);
  const titleId = useId();
  const reasonId = useId();
  const action = enabled ? 'resume' : 'pause';
  const title = enabled ? 'Resume merges' : 'Pause merges';

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [loading, onClose]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const response = await setMergeFreeze({ action, reason: reason.trim() });
      setResult(response);
      onSuccess(response);
    } catch (err) {
      onError(err);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
      className='fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4'
    >
      <div className='flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl'>
        <div className='border-b border-neutral-200 px-6 py-4'>
          <h3 id={titleId} className='text-lg font-medium text-neutral-900'>
            {title}
          </h3>
        </div>
        <div className='space-y-4 overflow-y-auto px-6 py-4 text-sm text-neutral-700'>
          {result == null ? (
            <>
              <p>Current merge freeze state for main: {enabled ? 'paused' : 'not paused'}.</p>
              {!enabled ? (
                <div className='rounded-md border border-red-200 bg-red-50 p-3 text-red-900'>
                  Pausing removes every PR from the merge queue. Dequeued PRs are not re-added on resume. Authors must
                  merge again.
                </div>
              ) : (
                <p>Resuming does not re-add dequeued PRs. Authors must merge again.</p>
              )}
              <p>
                The workflow run still needs approval from the freeze environment reviewers before this change takes
                effect. This control is independent of Disable deploys and does not change deploy pipelines.
              </p>
              <div>
                <label htmlFor={reasonId} className='mb-1 block font-medium text-neutral-900'>
                  Reason (optional)
                </label>
                <textarea
                  id={reasonId}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  maxLength={MERGE_FREEZE_REASON_MAX_LENGTH}
                  disabled={loading}
                  rows={3}
                  className='w-full rounded-md border border-neutral-300 px-3 py-2'
                />
                <p className='mt-1 text-xs text-neutral-500'>Included in the Slack notification after approval.</p>
              </div>
            </>
          ) : (
            <div role='status' className='rounded-md border border-blue-200 bg-blue-50 p-3 text-blue-950'>
              <p>
                Request submitted. Merges have not changed yet. The workflow must be approved by freeze environment
                reviewers and run successfully before the new state takes effect.
              </p>
              <a
                href={result.workflowUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='mt-2 inline-block font-medium underline'
              >
                View merge freeze workflow
              </a>
            </div>
          )}
        </div>
        <div className='flex justify-end gap-2 border-t border-neutral-200 px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            disabled={loading}
            className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50'
          >
            {result == null ? 'Cancel' : 'Close'}
          </button>
          {result == null && (
            <button
              type='button'
              onClick={() => void handleConfirm()}
              disabled={loading}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                enabled ? 'bg-neutral-900 hover:bg-neutral-800' : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {loading ? 'Submitting…' : `Confirm ${action} merges`}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
